import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { adminAPI } from '../../services/api';
import { FaChartLine, FaArrowLeft, FaUsers, FaFileAlt, FaBuilding, FaBriefcase, FaCalendarAlt } from 'react-icons/fa';

const ViewReports = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState('all'); // all, week, month, year

    useEffect(() => {
        loadReports();
    }, [dateRange]);

    const loadReports = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getDashboardStats();
            setStats(response.stats || {});
        } catch (err) {
            console.error('Failed to load reports:', err);
            toast.error('Failed to load reports. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const getTotalUsers = () => {
        if (!stats?.users || !Array.isArray(stats.users)) return 0;
        return stats.users.reduce((sum, u) => sum + (u.count || 0), 0);
    };

    const getTotalApplications = () => {
        if (!stats?.applications || !Array.isArray(stats.applications)) return 0;
        return stats.applications.reduce((sum, a) => sum + (a.count || 0), 0);
    };

    const getTotalEmployers = () => {
        if (!stats?.employers || !Array.isArray(stats.employers)) return 0;
        return stats.employers.reduce((sum, e) => sum + (e.count || 0), 0);
    };

    const getUsersByRole = () => {
        if (!stats?.users || !Array.isArray(stats.users)) return [];
        return stats.users.map(u => ({
            role: u.role,
            count: u.count || 0
        }));
    };

    const getApplicationsByStatus = () => {
        if (!stats?.applications || !Array.isArray(stats.applications)) return [];
        return stats.applications.map(a => ({
            status: a.status,
            count: a.count || 0
        }));
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <div className="max-w-7xl mx-auto px-6 py-8">
                <Link to="/dashboard?tab=reports" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
                    <FaArrowLeft className="mr-2" /> Back to Dashboard
                </Link>

                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                        <FaChartLine className="text-primary-600" />
                        System Reports & Analytics
                    </h1>
                    <p className="text-gray-600 mt-2">View comprehensive system statistics and analytics</p>
                </div>

                {/* Date Range Filter */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                    <div className="flex gap-4">
                        <button
                            onClick={() => setDateRange('all')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                dateRange === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            All Time
                        </button>
                        <button
                            onClick={() => setDateRange('week')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                dateRange === 'week' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Last Week
                        </button>
                        <button
                            onClick={() => setDateRange('month')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                dateRange === 'month' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Last Month
                        </button>
                        <button
                            onClick={() => setDateRange('year')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                dateRange === 'year' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Last Year
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Overview Cards */}
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Overview</h2>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <div className="flex items-center gap-2 mb-2">
                                        <FaUsers className="text-blue-600" />
                                        <span className="text-sm font-medium text-gray-700">Total Users</span>
                                    </div>
                                    <p className="text-3xl font-bold text-gray-900">{getTotalUsers()}</p>
                                </div>
                                <div className="bg-green-50 p-4 rounded-lg">
                                    <div className="flex items-center gap-2 mb-2">
                                        <FaFileAlt className="text-green-600" />
                                        <span className="text-sm font-medium text-gray-700">Applications</span>
                                    </div>
                                    <p className="text-3xl font-bold text-gray-900">{getTotalApplications()}</p>
                                </div>
                                <div className="bg-purple-50 p-4 rounded-lg">
                                    <div className="flex items-center gap-2 mb-2">
                                        <FaBuilding className="text-purple-600" />
                                        <span className="text-sm font-medium text-gray-700">Employers</span>
                                    </div>
                                    <p className="text-3xl font-bold text-gray-900">{getTotalEmployers()}</p>
                                </div>
                                <div className="bg-yellow-50 p-4 rounded-lg">
                                    <div className="flex items-center gap-2 mb-2">
                                        <FaBriefcase className="text-yellow-600" />
                                        <span className="text-sm font-medium text-gray-700">Jobs</span>
                                    </div>
                                    <p className="text-3xl font-bold text-gray-900">{stats?.jobs?.total || 0}</p>
                                </div>
                            </div>
                        </div>

                        {/* Users by Role */}
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Users by Role</h2>
                            <div className="space-y-3">
                                {getUsersByRole().map((item, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <span className="font-medium text-gray-700 capitalize">{item.role.replace('_', ' ')}</span>
                                        <span className="text-lg font-bold text-gray-900">{item.count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Applications by Status */}
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Applications by Status</h2>
                            <div className="space-y-3">
                                {getApplicationsByStatus().map((item, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <span className="font-medium text-gray-700 capitalize">{item.status.replace('_', ' ')}</span>
                                        <span className="text-lg font-bold text-gray-900">{item.count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Employer Status */}
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Employer Status</h2>
                            <div className="space-y-3">
                                {stats?.employers && Array.isArray(stats.employers) && stats.employers.map((item, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <span className="font-medium text-gray-700">
                                            {item.is_approved === 1 || item.is_approved === true ? 'Approved' : 'Pending'}
                                        </span>
                                        <span className="text-lg font-bold text-gray-900">{item.count || 0}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ViewReports;

